import { useState, useEffect } from "react";
import { useParams, useLocation } from "wouter";
import { trpc } from "../lib/trpc";
import { useTranslation } from "../i18n";
import { Button } from "../components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Badge } from "../components/ui/badge";
import { ArrowLeft, Download, Receipt, CheckCircle2, Loader2 } from "lucide-react";
import { useDateFormat } from '@/contexts/DateFormatContext';
import { toast } from "sonner";

export default function OrderDetails() {
  const params = useParams();
  const orderId = parseInt(params.id || "0");
  const [, navigate] = useLocation();
  const { t } = useTranslation();
  const utils = trpc.useUtils();

  const { data: order } = trpc.orders.getById.useQuery({ id: orderId });
  const [deliveryNoteOpen, setDeliveryNoteOpen] = useState(false);

  const markAsPaidMutation = trpc.orders.markAsPaid.useMutation({
    onSuccess: () => {
      toast.success(t.orders.markedAsPaid);
      utils.orders.getById.invalidate({ id: orderId });
      utils.orders.list.invalidate();
    },
    onError: (error) => {
      toast.error(error.message);
    },
  });

  const downloadAudit = trpc.orders.getDownloadAudit.useQuery(
    { orderId },
    { enabled: !!orderId && !!order }
  );

  const handleDownloadImage = async (imageId: number, filename: string) => {
    try {
      const result = await utils.client.downloads.getImageDownloadUrl.query({ orderId, imageId });
      if (result && result.url) {
        window.open(result.url, '_blank');
        toast.success(`${t.images.downloadSuccess}: ${filename}`);
      }
    } catch (error) {
      toast.error(t.images.downloadError);
    }
  };

  const handleDownloadAll = async () => {
    try {
      const result = await utils.client.downloads.getAllDownloadUrls.query({ orderId });
      if (result && result.urls) {
        result.urls.forEach((item, index) => {
          if (item) {
            setTimeout(() => window.open(item.url, '_blank'), index * 200);
          }
        });
        toast.success(`${t.images.downloadAllSuccess} (${result.urls.length})`);
      }
    } catch (error) {
      toast.error(t.images.downloadErrorMultiple);
    }
  };

  if (!order) {
    return <div className="p-8">{t.common.loading}</div>;
  }

  const getStatusBadge = (status: string) => {
    const variants: Record<string, "default" | "secondary" | "destructive" | "outline"> = {
      pending: "secondary",
      paid: "default",
      failed: "destructive",
      refunded: "outline",
    };

    return (
      <Badge variant={variants[status] || "default"}>
        {t.orders[status as keyof typeof t.orders] || status}
      </Badge>
    );
  };

  const { formatDate, formatDateTime } = useDateFormat();
  const hasPrintItems = !!order?.items?.some((it: any) => it.productType === "print" || it.printFormat);
  return (
    <div className="container mx-auto p-8">
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div className="flex items-center gap-4">
          <Button 
            variant="outline" 
            size="sm" 
            onClick={() => navigate("/orders")}
            className="gap-2 hover:bg-purple-50 dark:hover:bg-purple-950/30 hover:text-purple-600 dark:hover:text-purple-400 hover:border-purple-300 dark:hover:border-purple-700 transition-colors"
          >
            <ArrowLeft className="h-4 w-4" />
            {t.common.back}
          </Button>
          <div>
            <h1 className="text-3xl font-bold flex items-center gap-2">
              <Receipt className="h-7 w-7 text-purple-600" />
              {t.orders.orderDetails} #{order.orderNumber}
            </h1>
            <p className="text-muted-foreground">
              {formatDate(order.createdAt)}
            </p>
          </div>
        </div>
        <div className="flex gap-2">
          {order.status === "pending" && (
            <Button 
              onClick={() => markAsPaidMutation.mutate({ orderId })}
              disabled={markAsPaidMutation.isPending}
              variant="default"
              className="bg-green-600 hover:bg-green-700"
            >
              {markAsPaidMutation.isPending ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <CheckCircle2 className="h-4 w-4 mr-2" />
              )}
              {markAsPaidMutation.isPending ? t.orders.markingAsPaid : t.orders.markAsPaid}
            </Button>
          )}
          {order.status === "paid" && (
            <Button onClick={handleDownloadAll}>
              <Download className="h-4 w-4 mr-2" />
              {t.orders.downloadAll}
            </Button>
          )}
          {hasPrintItems && (
            <Button variant="outline" onClick={() => setDeliveryNoteOpen(true)}>
              {t.orders?.deliveryNote || "Lieferschein"}
            </Button>
          )}
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-6 mb-8">
        {/* Order Info */}
        <Card>
          <CardHeader>
            <CardTitle>{t.orders.orderInfo}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <p className="text-sm text-muted-foreground">{t.orders.orderStatus}</p>
              <div className="mt-1">{getStatusBadge(order.status)}</div>
            </div>
            <div>
              <p className="text-sm text-muted-foreground">{t.orders.orderDate}</p>
              <p className="font-medium">
                {formatDateTime(order.createdAt)}
              </p>
            </div>
            <div>
              <p className="text-sm text-muted-foreground">{t.orders.totalAmount}</p>
              <p className="text-2xl font-bold">
                {parseFloat(order.totalAmount).toFixed(2)} {order.currency}
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Customer Info */}
        <Card>
          <CardHeader>
            <CardTitle>{t.checkout.customerInfo}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <p className="text-sm text-muted-foreground">{t.orders.customerName}</p>
              <p className="font-medium">{order.customerName}</p>
            </div>
            <div>
              <p className="text-sm text-muted-foreground">{t.orders.customerEmail}</p>
              <p className="font-medium">{order.customerEmail}</p>
            </div>
            {(order as any).downloadInfo && (
              <div className="pt-3 border-t">
                <p className="text-sm text-muted-foreground">{t.orders.downloadedAt}</p>
                {(order as any).downloadInfo.downloadedAt ? (
                  <div className="flex items-center gap-2 mt-1">
                    <Download className="h-4 w-4 text-green-600" />
                    <p className="font-medium text-green-700 dark:text-green-400">
                      {formatDateTime(new Date((order as any).downloadInfo.downloadedAt))}
                    </p>
                  </div>
                ) : (
                  <p className="font-medium text-muted-foreground mt-1">
                    {t.orders.notDownloadedYet}
                  </p>
                )}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Payment Details */}
      {order.payment && (
        <Card className="mb-8">
          <CardHeader>
            <CardTitle>{t.orders.paymentDetails}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid md:grid-cols-3 gap-4">
              <div>
                <p className="text-sm text-muted-foreground">{t.orders.paymentMethod}</p>
                <p className="font-medium">{order.payment.paymentProvider}</p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">{t.orders.transactionId}</p>
                <p className="font-mono text-sm">{order.payment.transactionId || "N/A"}</p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">{t.orders.paymentStatus}</p>
                {getStatusBadge(order.payment.status)}
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Download-Job UI entfernt */}

      {/* Download History removed per request */}

      {/* Order Items */}
      <Card>
        <CardHeader>
          <CardTitle>{t.orders.items}</CardTitle>
          <CardDescription>
            {order.items?.length || 0} {order.items?.length === 1 ? t.orders.imageCount : t.orders.imageCountPlural}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{t.orders.image}</TableHead>
                <TableHead>{t.common.type || "Typ"}</TableHead>
                <TableHead>{t.common.quantity}</TableHead>
                <TableHead>{t.common.price}</TableHead>
                <TableHead className="text-right">{t.common.actions}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {order.items?.map((item: any) => (
                <TableRow key={item.id}>
                  <TableCell className="font-medium">
                    {item.filename || `${t.orders.image} ${item.imageId}`}
                  </TableCell>
                    <TableCell>
                      {item.itemType === "video" ? (
                        <Badge variant="secondary">{t.common.video}</Badge>
                      ) : item.productType === "print" || item.printFormat ? (
                        <div className="flex items-center gap-2">
                          <Badge variant="outline">{t.common.print}</Badge>
                          {item.printFormat && (
                            <span className="text-muted-foreground text-sm">{item.printFormat}</span>
                          )}
                        </div>
                      ) : (
                        <Badge variant="default">{t.common.digital}</Badge>
                      )}
                    </TableCell>
                  <TableCell>{item.quantity}</TableCell>
                  <TableCell>
                    {parseFloat(item.priceAtPurchase).toFixed(2)} {order.currency}
                  </TableCell>
                  <TableCell className="text-right">
                    {order.status === "paid" && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDownloadImage(item.imageId, item.filename)}
                      >
                        <Download className="h-4 w-4 mr-2" />
                        {t.orders.download}
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>

          {order.status !== "paid" && (
            <div className="mt-4 p-4 bg-muted rounded-lg text-center text-muted-foreground">
              {t.orders.imagesAvailableAfterPayment}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Delivery Note Dialog */}
      <Dialog open={deliveryNoteOpen} onOpenChange={setDeliveryNoteOpen}>
        <DialogContent className="max-w-xl">
          <DialogHeader>
            <DialogTitle>{t.orders?.deliveryNote || "Lieferschein"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <p className="text-sm text-muted-foreground">{t.checkout.addressLine1}</p>
              <p className="font-medium">{(order as any).customerAddressLine1 || "-"}</p>
              {(order as any).customerAddressLine2 && (
                <p className="font-medium">{(order as any).customerAddressLine2}</p>
              )}
              <p className="font-medium">
                {(order as any).customerPostalCode || "-"} {(order as any).customerCity || ""}
              </p>
              <p className="font-medium">{(order as any).customerCountry || "-"}</p>
            </div>
            <div className="pt-2 border-t">
              <p className="text-sm text-muted-foreground">{t.orders.items}</p>
              <ul className="mt-2 space-y-1">
                {order?.items
                  ?.filter((it: any) => it.productType === "print" || it.printFormat)
                  .map((it: any) => (
                    <li key={it.id} className="flex justify-between">
                      <span>
                        {it.filename || `${t.orders.image} ${it.imageId}`}
                        {it.printFormat ? ` • ${it.printFormat}` : ""}
                      </span>
                      <span>x{it.quantity}</span>
                    </li>
                  ))}
              </ul>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
